import type React from "react";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";

// Two-theme model: the custom warm-paper "light" theme (default) and the
// "dark" scaffold. Any legacy stored theme (the previous 29-theme set) is
// migrated to "light" on load.
export const themes = ["light", "dark"] as const;

export type ThemeName = (typeof themes)[number];

const DEFAULT_THEME: ThemeName = "light";

/** Resolve a stored value to a supported theme, migrating legacy values. */
function resolveStoredTheme(stored: string | null): ThemeName {
	return stored && themes.includes(stored as ThemeName)
		? (stored as ThemeName)
		: DEFAULT_THEME;
}

// Define the context type
interface ThemeContextType {
	currentTheme: ThemeName;
	changeTheme: (theme: ThemeName) => void;
	themes: typeof themes;
}

// Create the context with a default value
const ThemeContext = createContext<ThemeContextType>({
	currentTheme: "light",
	changeTheme: () => {},
	themes,
});

// Storage key for persisting theme preference
const THEME_STORAGE_KEY = "pilatestime-admin-theme";

// Provider component
export function ThemeProvider({ children }: { children: React.ReactNode }) {
	// Initialize with stored theme (legacy values migrate to light).
	const [currentTheme, setCurrentTheme] = useState<ThemeName>(() =>
		resolveStoredTheme(localStorage.getItem(THEME_STORAGE_KEY)),
	);

	// Change theme function - memoized with useCallback
	const changeTheme = useCallback((theme: ThemeName) => {
		if (themes.includes(theme)) {
			setCurrentTheme(theme);
			localStorage.setItem(THEME_STORAGE_KEY, theme);
			document.documentElement.setAttribute("data-theme", theme);
		}
	}, []);

	// Initialize theme on mount
	useEffect(() => {
		changeTheme(currentTheme);
	}, [currentTheme, changeTheme]);

	const value = {
		currentTheme,
		changeTheme,
		themes,
	};

	return (
		<ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
	);
}

// Custom hook to use the theme context
export function useTheme() {
	const context = useContext(ThemeContext);
	if (context === undefined) {
		throw new Error("useTheme must be used within a ThemeProvider");
	}
	return context;
}
